import { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { ShieldCheck, ShieldAlert, AlertTriangle, Activity, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const FETCH_OPTS = { credentials: 'include' };

/**
 * Compact Plaid health card for the Owner Admin Cost Dashboard.
 * Surfaces: environment (sandbox/production), JWT verification state,
 * webhook traffic in the last 24h, and any banks needing re-auth.
 */
export default function PlaidStatusBadge() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API}/api/banking/plaid/webhook-status`, FETCH_OPTS);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  if (loading && !data) {
    return (
      <Card data-testid="plaid-status-loading">
        <CardContent className="p-4 text-sm text-muted-foreground">Loading Plaid status…</CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-red-500/40 bg-red-500/5" data-testid="plaid-status-error">
        <CardContent className="p-4 flex items-center gap-2 text-red-500 text-sm">
          <AlertTriangle className="w-4 h-4" /> Plaid status: {error}
        </CardContent>
      </Card>
    );
  }

  const isProd = !!data?.production;
  const verified = !!data?.verification_enabled;
  const banks = data?.banks || {};
  const reauthCount = banks.needs_reauth || 0;
  const errorBanks = banks.errored || 0;
  const total24h = data?.last_24h_total || 0;
  const rejected24h = data?.last_24h_rejected || 0;
  const errors24h = data?.last_24h_errors || 0;

  return (
    <Card data-testid="plaid-status-badge" className="border-l-4 border-l-emerald-500/60">
      <CardContent className="p-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              {verified
                ? <ShieldCheck className="w-5 h-5 text-emerald-500" />
                : <ShieldAlert className="w-5 h-5 text-amber-500" />}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm">Plaid Integration</span>
                <Badge
                  variant={isProd ? 'default' : 'secondary'}
                  className={isProd
                    ? 'bg-emerald-500/15 text-emerald-500 hover:bg-emerald-500/20 border border-emerald-500/30'
                    : 'bg-amber-500/15 text-amber-500 border border-amber-500/30'
                  }
                  data-testid="plaid-env-badge"
                >
                  {isProd ? 'PRODUCTION' : (data?.env || 'sandbox').toUpperCase()}
                </Badge>
                <Badge
                  variant="outline"
                  className={verified
                    ? 'border-emerald-500/30 text-emerald-500'
                    : 'border-amber-500/30 text-amber-500'}
                  data-testid="plaid-verification-badge"
                >
                  {verified ? 'JWT Verified' : 'Verification OFF'}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1 break-all">
                {data?.webhook_url || '(no webhook URL configured)'}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={refresh}
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            data-testid="plaid-status-refresh"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4 text-xs">
          <Stat label="Banks" value={banks.total ?? 0} testId="plaid-banks-total" />
          <Stat
            label="Need Re-auth"
            value={reauthCount}
            tone={reauthCount > 0 ? 'amber' : 'muted'}
            testId="plaid-banks-reauth"
          />
          <Stat
            label="Errored"
            value={errorBanks}
            tone={errorBanks > 0 ? 'red' : 'muted'}
            testId="plaid-banks-errored"
          />
          <Stat label="24h Webhooks" value={total24h} testId="plaid-24h-total" icon={Activity} />
          <Stat
            label="24h Rejected"
            value={rejected24h}
            tone={rejected24h > 0 ? 'red' : 'muted'}
            testId="plaid-24h-rejected"
          />
        </div>

        {(reauthCount > 0 || errors24h > 0) && (
          <div className="mt-3 flex items-start gap-2 text-xs bg-amber-500/5 border border-amber-500/20 rounded-md p-2 text-amber-500">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
            <span>
              {reauthCount > 0 && `${reauthCount} bank${reauthCount === 1 ? '' : 's'} need re-authentication. `}
              {errors24h > 0 && `${errors24h} error webhook${errors24h === 1 ? '' : 's'} in last 24h.`}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, tone = 'muted', icon: Icon, testId }) {
  const colorMap = {
    muted: 'text-foreground',
    amber: 'text-amber-500',
    red: 'text-red-500',
    emerald: 'text-emerald-500',
  };
  return (
    <div className="flex flex-col gap-0.5" data-testid={testId}>
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
        {Icon ? <Icon className="w-3 h-3" /> : null}
        {label}
      </span>
      <span className={`text-base font-bold ${colorMap[tone]}`}>{value}</span>
    </div>
  );
}
